// packages/core/src/quizzes/content/freedomArchetype.ts
// ─────────────────────────────────────────────────────────────────────────
// "What Kind of Freedom Are You Chasing?" (tradeoff engine)
//
// Seven forced choices across six archetypes. Weight matrix (max score as
// primary): time 6, security 9, place 7, autonomy 7, experience 6,
// mission 7. A consistent picker always wins their own axis.
//
// Key facts are plain 4% arithmetic, verified:
//   $100/mo forever  → $100 × 12 ÷ 0.04 = $30,000 invested
//   $1,500/mo cheaper city → $450,000 smaller target
//   replace a $6,000/mo paycheck → $1,800,000
//   give $500/mo forever → $150,000
//   17 vs 51 years from the savings-rate math (same figures as the Money
//   Intuition quiz, 5% return after inflation, safe 4% yearly spend)
// ─────────────────────────────────────────────────────────────────────────

import type { TradeoffQuizContent } from '../DiscoveryTypes';

export const FREEDOM_ARCHETYPE: TradeoffQuizContent = {
	meta: {
		id: 'freedom-archetype',
		title: 'What Kind of Freedom Are You Chasing?',
		hook: 'Seven choices. Six kinds of freedom. Find the one you are actually working toward.',
		engine: 'tradeoff',
		minutes: 3,
		status: 'live',
	},

	questions: [
		{
			type: 'tradeoff',
			id: 'fa-windfall',
			prompt: 'A surprise $50,000 lands in your account. What actually happens to it?',
			options: [
				{
					label: 'It sits in savings. Knowing it is there is the point.',
					weights: { security: 2 },
				},
				{
					label: 'It buys a long trip or a big experience this year.',
					weights: { experience: 2 },
				},
				{
					label: 'It seeds the project or business you keep talking about.',
					weights: { autonomy: 2 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'fa-tuesday',
			prompt: 'Your ideal Tuesday, ten years from now:',
			options: [
				{
					label: 'Working on something you chose, on your own schedule.',
					weights: { autonomy: 2 },
				},
				{
					label: 'Somewhere new. Laptop optional.',
					weights: { place: 2 },
				},
				{
					label: 'The same comfortable routine, fully paid for.',
					weights: { security: 2 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'fa-loss',
			prompt: 'Which loss would sting the most?',
			options: [
				{
					label: 'Losing your savings cushion.',
					weights: { security: 2 },
				},
				{
					label: 'Losing the ability to pick up and go.',
					weights: { place: 2 },
				},
				{
					label: 'Losing say over your own calendar.',
					weights: { time: 2 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'fa-offer',
			prompt: 'A job offer arrives: 40% more pay, but your evenings belong to the job. You:',
			options: [
				{
					label: 'Take it. The bigger cushion is worth a few hard years.',
					weights: { security: 2 },
				},
				{
					label: 'Decline. Evenings are not for sale.',
					weights: { time: 2 },
				},
				{
					label: 'Counter for remote and flexible hours instead.',
					weights: { autonomy: 1, place: 1 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'fa-envy',
			prompt: 'The version of rich you actually envy:',
			options: [
				{
					label: 'The founder who answers to nobody.',
					weights: { autonomy: 2 },
				},
				{
					label: 'The couple who quietly funds a scholarship every year.',
					weights: { mission: 2 },
				},
				{
					label: 'The neighbor who stopped working at 50 and never announced it.',
					weights: { time: 2 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'fa-first-job',
			prompt: 'Money\u2019s first job is to:',
			options: [
				{
					label: 'Protect the people who count on you.',
					weights: { security: 1, mission: 1 },
				},
				{
					label: 'Fund what you care about beyond yourself.',
					weights: { mission: 3 },
				},
				{
					label: 'Buy good days, now, while you can enjoy them.',
					weights: { experience: 2 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'fa-overspend',
			prompt: 'You would happily spend more than most people on:',
			options: [
				{
					label: 'Flights, and the freedom to leave.',
					weights: { place: 2 },
				},
				{
					label: 'Meals, trips, and front-row seats.',
					weights: { experience: 2 },
				},
				{
					label: 'The causes and people you back.',
					weights: { mission: 2 },
				},
			],
		},
	],

	axes: {
		time: {
			label: 'Time Rich',
			insight:
				'Your version of freedom is owning your hours. The money is only the mechanism. The savings-rate math is your friend here: save half of what you earn at a 5% return after inflation, and about 17 years of work covers the rest of your life. Save 10% and the same math says 51 years.',
			blindSpot:
				'Hours you win back do not stay yours by default. Other people\u2019s requests refill an empty calendar fast, and paid work was quietly providing structure you will have to rebuild on purpose.',
			keyFact:
				'At a safe 4% yearly spend, every $100 of monthly spending you keep needs about $30,000 invested behind it before your time is truly paid for.',
		},
		security: {
			label: 'Safety Built',
			insight:
				'Your version of freedom is being unshakeable. Nothing can knock the household over, and that calm is worth real money to you. Buffers, insurance, and a fat cushion are not fear. They are the product you are buying.',
			blindSpot:
				'"Enough" measured by feel keeps moving. Every extra year of margin is bought with a year of your life, and the margin only pays off if some version of you eventually gets to relax.',
			keyFact:
				'Six months of a $4,000 monthly life means $24,000 sitting still on purpose. Naming the number is what lets it stop growing forever.',
		},
		place: {
			label: 'Unanchored',
			insight:
				'Your version of freedom is geographic. Any city, any country, nothing pinning you down. Where you live is also the biggest price tag in most budgets, which makes this the most financially powerful archetype of the six.',
			blindSpot:
				'Mobility prices out roots. Deep friendships, career compounding, and community all pay off for staying put, and the bill for skipping them arrives years later.',
			keyFact:
				'Living somewhere $1,500 a month cheaper shrinks the amount you need invested for good by about $450,000 at a safe 4% yearly spend.',
		},
		autonomy: {
			label: 'Own Boss',
			insight:
				'Your version of freedom is nobody assigning your work. The subject matters less than the fact that you chose it. A paycheck feels like a leash even when the leash is comfortable.',
			blindSpot:
				'Owning the work means owning the risk. Income gets lumpy before it gets good, and the boss you escape gets replaced by customers, who are many and less polite.',
			keyFact:
				'Fully replacing a $6,000 monthly paycheck at a safe 4% yearly spend takes about $1.8 million invested, or a business that reliably nets it. Most people build a mix.',
		},
		experience: {
			label: 'Experience Rich',
			insight:
				'Your version of freedom is a full life now, not a bigger number later. That is a legitimate position, and the math can hold it honestly as long as the trade is priced instead of ignored.',
			blindSpot:
				'Every permanent spending habit quietly sets the size of the future you have to fund. The trade is only honest when you know what each habit costs in invested dollars.',
			keyFact:
				'At a safe 4% yearly spend, a $100 monthly habit you plan to keep forever needs about $30,000 invested to carry it. That is the real price tag.',
		},
		mission: {
			label: 'Mission Funded',
			insight:
				'Your version of freedom is money pointed at something beyond you. People, causes, a community. The pile is not the point. What the pile makes possible is.',
			blindSpot:
				'Generosity without a floor under it turns into dependence, yours or theirs. The mission is funded by the boring machine underneath it, and the machine has to be built first.',
			keyFact:
				'Giving $500 a month forever is, at a safe 4% yearly spend, a $150,000 endowment. Build that block and the giving never has to stop.',
		},
	},

	cta: {
		label: 'Put a date on your freedom',
		to: '/simulate',
		blurb: 'Every archetype still runs on the same math underneath. Nisk takes your income, spending, and savings and shows the date your version of freedom actually arrives.',
	},

	shareText:
		'My freedom archetype is {primary}, according to Nisk. Seven forced choices, six kinds of freedom, one honest blind spot: https://nisk.co/quizzes/freedom-archetype',
};
